import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { useOnline } from '@/lib/use-online';
import { usePropsSyncInfo, useSincronizarPropriedades } from '../api';

function fmtQuando(ts: number): string {
  const min = Math.floor((Date.now() - ts) / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  return `há ${Math.floor(h / 24)} d`;
}

// Card "Propriedades offline" — baixa fazendas → talhões (+ dados) + mapas
// (satélite/NDVI/NDMI) pro uso offline. Sem `cpf` baixa tudo (tela de Produtores);
// com `cpf` baixa só as fazendas daquele produtor (tela de Fazendas).
export function PropriedadesOfflineCard({ cpf }: { cpf?: string } = {}) {
  const { data: syncInfo } = usePropsSyncInfo(cpf);
  const sync = useSincronizarPropriedades(cpf);
  const online = useOnline();
  const [prog, setProg] = useState<{ done: number; total: number; label: string } | null>(null);

  function sincronizar() {
    setProg({ done: 0, total: 0, label: '' });
    sync.mutate((done, total, label) => setProg({ done, total, label }), { onSettled: () => setProg(null) });
  }

  return (
    <div className="mb-2.5 rounded-[14px] border border-[var(--agro-card-border)] bg-surface2 px-3 py-2.5">
      <div className="flex items-center gap-2.5">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-[11px] bg-[var(--accent-100)]">
          <Download size={20} strokeWidth={1.75} className="text-accent" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-[0.9rem] font-semibold text-text">Propriedades offline</span>
          <span className="truncate text-[0.8rem] text-text-2">
            {sync.isPending && prog
              ? `Baixando ${prog.label || '…'} · ${prog.done}/${prog.total || '…'}`
              : syncInfo
                ? `${cpf ? '' : `${syncInfo.produtores} produtores · `}${syncInfo.fazendas} fazendas · ${fmtQuando(syncInfo.ts)}`
                : cpf ? 'Baixe as fazendas deste produtor pro uso offline' : 'Baixe as propriedades pra usar no campo sem internet'}
          </span>
        </div>
        <button
          type="button"
          disabled={sync.isPending || !online}
          onClick={sincronizar}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--accent)] px-3.5 py-2 text-[0.8rem] font-semibold text-[var(--on-solid)] disabled:opacity-60"
        >
          {sync.isPending ? <Loader2 size={15} strokeWidth={2} className="animate-spin" /> : <Download size={15} strokeWidth={2} />}
          Sincronizar
        </button>
      </div>
      {sync.isPending && prog && prog.total > 0 && (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface3">
          <div
            className="h-full rounded-full bg-[var(--accent)] transition-[width] duration-200"
            style={{ width: `${Math.round((prog.done / prog.total) * 100)}%` }}
          />
        </div>
      )}
      {sync.isError && <p className="mt-1.5 text-[0.76rem] text-[#b3261e]">Falha ao sincronizar. Tente de novo com internet.</p>}
    </div>
  );
}
